import axios from 'axios';

import { parseAIError } from '@/utils/extractAI';

const AI_BASE = () => `${import.meta.env.VITE_APP_EXTRACT_URL}/api/ai/event`;
const TAGGING_BASE = () => `${import.meta.env.VITE_APP_EXTRACT_URL}/api/ai/events`;

const PLAN_POLL_INTERVAL = 2500;
const PLAN_MAX_ATTEMPTS = 96;

export interface EventCoverRequest {
  vessel_id: string;
  user_id?: string;
  title: string;
  description?: string;
  event_type?: string;
}

export interface EventCoverResponse {
  image_base64: string;
  content_type: string;
  width: number;
  height: number;
  model: string;
}

export interface PlanCrewMember {
  person_id: string;
  name: string;
  role: string;
}

export interface PlanGenerateRequest {
  vessel_id: string;
  user_id?: string;
  event: {
    title: string;
    description?: string;
    event_type?: string;
    start: string;
    end?: string;
    locations: string[];
    crew: PlanCrewMember[];
    guests_count: number;
  };
}

export interface PlanJobResponse {
  job_id: string;
  status: 'pending' | 'done' | 'error';
}

export interface PlanItineraryItem {
  activity: string;
  description?: string | null;
  start?: string | null;
  end?: string | null;
}

export type PlanTaskWhen = 'before' | 'during' | 'after' | null;

export interface PlanTaskItem {
  title: string;
  assignee_id?: string | null;
  assignee_role?: string | null;
  when?: PlanTaskWhen;
  category?: string | null;
  start?: string | null;
  end?: string | null;
}

export interface PlanResult {
  session_id: string;
  itinerary: PlanItineraryItem[];
  tasks: PlanTaskItem[];
  tokens?: number;
  latency_ms?: number;
}

export interface PlanStatusResponse {
  job_id: string;
  status: 'pending' | 'done' | 'error';
  result: PlanResult | null;
  error?: string | null;
  error_status?: number | null;
}

export interface PlanRefineRequest {
  session_id: string;
  target: 'itinerary' | 'tasks';
  message: string;
}

export interface PlanRefineResponse {
  reply: string;
  itinerary: PlanItineraryItem[];
  tasks: PlanTaskItem[];
  tokens?: number;
}

export const generateEventCover = async (
  body: EventCoverRequest,
  signal?: AbortSignal,
): Promise<EventCoverResponse> => {
  try {
    const res = await axios.post<EventCoverResponse>(`${AI_BASE()}/cover/generate`, body, {
      signal,
    });
    return res.data;
  } catch (error) {
    throw new Error(parseAIError(error).message);
  }
};

export const refineEventPlan = async (
  body: PlanRefineRequest,
  signal?: AbortSignal,
): Promise<PlanRefineResponse> => {
  try {
    const res = await axios.post<PlanRefineResponse>(`${AI_BASE()}/plan/refine`, body, { signal });
    return res.data;
  } catch (error) {
    throw new Error(parseAIError(error).message);
  }
};

// Starts generation, then polls status until done / error / timeout.
export const runPlanGeneration = async (
  body: PlanGenerateRequest,
  signal: AbortSignal,
): Promise<PlanResult> => {
  let job: PlanJobResponse;
  try {
    const res = await axios.post<PlanJobResponse>(`${AI_BASE()}/plan/generate`, body, { signal });
    job = res.data;
  } catch (error) {
    throw new Error(parseAIError(error).message);
  }

  if (!job?.job_id) throw new Error('Couldn’t start the plan. Please try again.');

  for (let attempt = 0; attempt < PLAN_MAX_ATTEMPTS; attempt += 1) {
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');

    let status: PlanStatusResponse;
    try {
      const res = await axios.get<PlanStatusResponse>(`${AI_BASE()}/plan/status/${job.job_id}`, {
        signal,
      });
      status = res.data;
    } catch (error) {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      throw new Error(parseAIError(error).message);
    }

    if (status.status === 'done' && status.result) return status.result;
    if (status.status === 'error')
      throw new Error(status.error || 'Couldn’t generate the plan. Please try again.');

    await new Promise((resolve) => setTimeout(resolve, PLAN_POLL_INTERVAL));
  }

  throw new Error('Generating the plan took too long. Please try again.');
};

export interface EventTaggingRequest {
  vessel_id: string;
  user_id: string;
  /** The tags went through the create form's tag step: they are the user's, never re-tag. */
  reviewed?: boolean;
}

/**
 * Tells core-ai about a saved event. It answers at once (`queued`, or `disabled`
 * where tagging is off). Reviewed, it only records that the tags are the user's;
 * otherwise the AI tags the event and the tags land a few seconds later, and the
 * nightly job tags whatever this call missed. Events whose tags a person has
 * edited are never re-tagged.
 */
export const requestEventTagging = async (
  eventId: string,
  body: EventTaggingRequest,
): Promise<void> => {
  await axios.post(`${TAGGING_BASE()}/${eventId}/tags`, body);
};

/** The event as it stands at the end of the create form, before it is saved. */
export interface EventTagsDraft {
  vessel_id: string;
  user_id: string;
  title: string;
  description?: string;
  type?: string;
  location_name?: string;
  /** The event's stops, packed with STOP_SEP as they are saved. */
  location_address?: string;
  start?: string;
  end?: string;
  itinerary?: string[];
}

export interface SuggestedEventTags {
  /** `ok`; `disabled` where tagging is off; `failed` when the AI did not answer. */
  status: 'ok' | 'disabled' | 'failed';
  tags: { id: string; name: string }[];
}

/**
 * Tags the AI would put on an unsaved event (about 1–2 s). Never throws: without
 * an answer the form just starts with no tags and the user picks them.
 */
export const suggestEventTags = async (
  draft: EventTagsDraft,
  signal?: AbortSignal,
): Promise<SuggestedEventTags> => {
  try {
    const res = await axios.post<SuggestedEventTags>(`${TAGGING_BASE()}/tags/suggest`, draft, {
      signal,
    });
    return { status: res.data.status ?? 'ok', tags: res.data.tags ?? [] };
  } catch (error) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    console.error('Event tag suggestion error:', error);
    return { status: 'failed', tags: [] };
  }
};
